/**
 * The shapes the screens are written against.
 *
 * These come from the design repo unchanged in *structure* — every component
 * and page in `src/components` and `src/pages` was written against these field
 * names, and rewriting them would mean rewriting the design. What changed is
 * the *values*: the union members are Studio Happens' own vocabulary rather
 * than the generic Project/Task one the design shipped with.
 *
 * So a `Task` here is a **Piece** — one actual reel or post — and its `status`
 * is one of the seven stages the database stores and the WhatsApp agent moves
 * things between. A `Project` is a **Cycle**: one month of one retainer. The
 * field is still called `projectId` because two hundred lines of working
 * layout code say `projectId`, and the name of a prop is not the name of the
 * thing.
 *
 * The translation between these and the wire shapes in `../api` happens in
 * exactly one place — `context/AppDataContext.tsx` — so there is one file to
 * read when a label and a column disagree.
 */

/** What to pick up first. Not effort — that is `weight`, and it is separate. */
export type Priority = 'Low' | 'Medium' | 'High' | 'Urgent'

/**
 * Where a piece is, in the studio's words.
 *
 * These are display strings, not the wire values: the database stores
 * `internal_review` and the board column reads "Review". `STAGE_TO_STATUS` and
 * `STATUS_TO_STAGE` in `AppDataContext` are the only two places that know
 * both halves.
 */
export type TaskStatus =
  | 'Briefed'
  | 'Shot'
  | 'Editing'
  | 'Review'
  | 'With client'
  | 'Scheduled'
  | 'Posted'

/** Every stage, in the order work moves through them. */
export const TASK_STATUSES: TaskStatus[] = [
  'Briefed',
  'Shot',
  'Editing',
  'Review',
  'With client',
  'Scheduled',
  'Posted',
]

/** A piece is finished when it is up. Nothing after `Posted`. */
export const DONE_STATUS: TaskStatus = 'Posted'

export type AccountStatus = 'Active' | 'Prospect' | 'Unconfirmed' | 'Former' | 'Lost'

/** A monthly arrangement that renews, or a single piece of work. */
export type Engagement = 'Recurring' | 'One-off'

export type Role = 'Admin' | 'Manager' | 'Team Member' | 'Viewer'

export interface TeamMember {
  id: string
  name: string
  /** Their photo, when they have one. */
  photo?: string
  email: string
  role: Role
  department: string
  title: string
  color: string
  status: 'Active' | 'Away' | 'Inactive'
  activeTasks: number
  completedTasks: number
  pendingTasks: number
  /** 0-100, committed effort points against capacity. Over 100 is real and
   * is shown as over — clamping it would hide the only case worth seeing. */
  workload: number
  /**
   * How much of what they hold has gone out: posted over posted-plus-open.
   *
   * Not a rating, and deliberately not coloured like one. It moves when
   * somebody is handed three new briefs, which is the opposite of a
   * performance signal, so it is labelled as the ratio it is.
   */
  performance: number
  /** Distinct clients they have open work for. */
  clients: number
  /** True once that count is past what one person can follow up on. */
  tooManyClients: boolean
  projects: string[]
  joined: string
}

/** A cycle: one month of one retainer. */
export interface Project {
  id: string
  name: string
  clientId: string
  status: 'Active' | 'Completed' | 'On Hold' | 'Planning'
  progress: number
  dueDate: string
  teamIds: string[]
  budgetUsed: number
  description: string
}

/** A piece: one actual reel or post. */
export interface TaskNote {
  id: string
  authorId: string
  authorName: string
  text: string
  createdAt: string
}

export interface Task {
  id: string
  title: string
  description: string
  clientId: string
  projectId: string
  assigneeId: string
  dueDate: string
  priority: Priority
  status: TaskStatus
  tags: string[]
  notes: TaskNote[]
  createdAt: string
  /** Effort points. The roster's workload is the sum of these, not a count. */
  weight: number
}

export interface Client {
  id: string
  name: string
  initials: string
  color: string
  industry: string
  contactName: string
  contactEmail: string
  contactPhone: string
  accountStatus: AccountStatus
  engagement: Engagement
  activeProjects: number
  completedDeliverables: number
  pendingDeliverables: number
  assignedTeamIds: string[]
  lastActivity: string
  /** Absent — not zero — when the signed-in person is not a partner. The
   * server removes the key rather than nulling it, and so does this. */
  monthlyRetainer?: number
  retainerCurrency?: string
  /** Set when the client has been put away. Absent for everyone current. */
  archivedAt?: string
}

export type DeliverableStatus = 'Not Started' | 'In Progress' | 'In Review' | 'Completed'

/** A commitment line: "8 reels this month", and how many of them exist yet. */
export interface Deliverable {
  id: string
  clientId: string
  name: string
  type: string
  dueDate: string
  status: DeliverableStatus
  assignedToId: string
  completionDate: string | null
  target: number
  delivered: number
  inFlight: number
}

export type ContentPlatform =
  | 'Instagram'
  | 'TikTok'
  | 'Facebook'
  | 'LinkedIn'
  | 'YouTube'
  | 'X'
  | 'Other'
export type ContentType = 'Reel' | 'Carousel' | 'Static Post' | 'Story' | 'Video' | 'Article'
export type SimpleStatus = 'Not Started' | 'In Progress' | 'Done'
export type ApprovalStatus = 'Pending' | 'Approved' | 'Changes Requested'
export type PostingStatus = 'Scheduled' | 'Posted' | 'Not Scheduled'

/**
 * The same piece, read as a row of the content plan.
 *
 * Not a separate record: `id` is the piece's id, and writing to either view
 * writes to the same row. The content calendar and the board are two ways of
 * looking at one table, which is the whole point of retiring the sheet.
 */
export interface ContentItem {
  id: string
  date: string
  clientId: string
  /** The month that owes it, `YYYY-MM-01` — the month its deliverables count it in. */
  cycleMonth?: string
  /** Somebody said that month is right although the post date is in another. */
  monthPinned?: boolean
  /** The requirement it counts towards; null or absent when none. */
  commitmentId?: string | null
  commitment?: string | null
  platform: ContentPlatform
  contentType: ContentType
  title: string
  topic: string
  caption: string
  hook: string
  cta: string
  status: TaskStatus
  assignedToId: string
  designStatus: SimpleStatus
  editingStatus: SimpleStatus
  approvalStatus: ApprovalStatus
  postingStatus: PostingStatus
  postedDate: string | null
  reelLink: string
  notes: string
}

export interface Notification {
  id: string
  type: 'task' | 'deliverable' | 'approval' | 'overdue' | 'team'
  message: string
  time: string
  read: boolean
  /** Where clicking it should go. Absent for notifications with nowhere to be. */
  href?: string
}

export interface ActivityLogEntry {
  id: string
  actorId: string
  action: string
  target: string
  time: string
}

export interface WeeklyTimeEntry {
  teamMemberId: string
  hoursLogged: number
  capacityHours: number
}

/**
 * The option lists the editable cells and dropdowns are built from.
 *
 * In the design repo these lived in `data/mockContent.ts` beside the fake
 * rows, which is why they came out of the bundle when the mocks did. They are
 * not mock data — they are the vocabulary, and the server refuses anything
 * outside them, so they belong next to the types they enumerate.
 */
export const PLATFORMS: ContentPlatform[] = [
  'Instagram',
  'YouTube',
  'Facebook',
  'LinkedIn',
  'X',
  'TikTok',
  'Other',
]

export const CONTENT_TYPES: ContentType[] = [
  'Reel',
  'Carousel',
  'Static Post',
  'Story',
  'Video',
  'Article',
]

export const ROLES: Role[] = ['Admin', 'Manager', 'Team Member', 'Viewer']

export const PRIORITIES: Priority[] = ['Low', 'Medium', 'High', 'Urgent']

export const ACCOUNT_STATUSES: AccountStatus[] = ['Active', 'Prospect', 'Unconfirmed', 'Former', 'Lost']

export const ENGAGEMENTS: Engagement[] = ['Recurring', 'One-off']

/**
 * A half-filled record, on its way to being created.
 *
 * `Partial<T>` is not this. Under `exactOptionalPropertyTypes` — which this
 * repo turns on and the design repo did not — `Partial<T>` means "the key may
 * be missing", not "the key may be there holding undefined", so a form that
 * builds `{ dueDate: maybeUndefined }` in one object literal is refused. Every
 * `add*` on the data context takes one of these instead.
 */
export type Draft<T> = { [K in keyof T]?: T[K] | undefined }
